import { Money } from "../../../components/ui/Money";
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale';
import React, { useState } from 'react';
import { useOverdueMembers, useCreateContactAttempt } from '../hooks';
import Decimal from 'decimal.js';
import { Button } from '../../../components/ui/Button';
import { Card, CardHeader, CardContent } from '../../../components/ui/Card';
import { PageLoader } from '../../../components/ui/PageLoader';
import { EntityActionMenu } from '../../../components/ui/EntityActionMenu';
import { Bell, Eye } from 'lucide-react';

export function OverdueMembersList() {
  const [bucket, setBucket] = useState<'1-30' | '31-60' | '61-90' | '90+'>('1-30');
  const { data: members, isLoading, isError, error, refetch } = useOverdueMembers({ bucket });
  const memberList = Array.isArray(members) ? members : (members?.data ?? []);
  const [selectedInstallment, setSelectedInstallment] = useState<{ installmentId: string; memberId: string } | null>(null);

  const handleLogReminder = (installmentId: string, memberId: string) => {
    setSelectedInstallment({ installmentId, memberId });
  };

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
    <div className="flex justify-between items-center">
    <h1 className="text-2xl font-bold">Overdue Installments</h1>
    </div>

    <div className="flex space-x-4 border-b pb-2">
    <Button
    variant="ghost"
    className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${bucket === '1-30' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
    onClick={() => setBucket('1-30')}
    >
    1-30 Days
    </Button>
    <Button
    variant="ghost"
    className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${bucket === '31-60' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
    onClick={() => setBucket('31-60')}
    >
    31-60 Days
    </Button>
    <Button
    variant="ghost"
    className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${bucket === '61-90' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
    onClick={() => setBucket('61-90')}
    >
    61-90 Days
    </Button>
    <Button
    variant="ghost"
    className={`px-4 py-2 rounded-none bg-transparent hover:bg-transparent ${bucket === '90+' ? 'border-b-2 border-accent-500 font-bold' : ''}`}
    onClick={() => setBucket('90+')}
    >
    90+ Days
    </Button>
    </div>

 {isError ? (
 <Card>
 <div className="p-8 text-center">
 <p className="text-sm font-semibold text-danger-600">Failed to load overdue members</p>
 <p className="text-xs text-text-muted mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
 <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
 </div>
 </Card>
 ) : (
 <Card className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-surface-2">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Customer</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Group (Code)</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Ticket</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Days Overdue</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Due Amount</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Last Contact</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">Actions</th>
 </tr>
 </thead>
 <tbody className="bg-surface-1 divide-y divide-border">
  {memberList.map(member => (
 <tr key={member.installmentId}>
 <td className="px-6 py-4 whitespace-nowrap">{member.customerName}</td>
 <td className="px-6 py-4 whitespace-nowrap">{member.groupCode}</td>
 <td className="px-6 py-4 whitespace-nowrap">{member.ticketNumber}</td>
 <td className="px-6 py-4 whitespace-nowrap text-danger-text font-semibold">{member.daysOverdue}</td>
 <td className="px-6 py-4 whitespace-nowrap"><Money paise={member.dueAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} /></td>
 <td className="px-6 py-4 whitespace-nowrap">{member.lastContactDate || 'Never'}</td>
 <td className="px-6 py-4 whitespace-nowrap text-right">
  <EntityActionMenu
    actions={[
      {
        label: 'Log Reminder',
        icon: <Bell className="w-4 h-4" />,
        onClick: () => handleLogReminder(member.installmentId, member.memberId),
      },
      {
        label: 'View Member Details',
        icon: <Eye className="w-4 h-4" />,
        onClick: () => {
          window.location.href = `/customers/${member.memberId}`;
        },
      },
    ]}
  />
 </td>
 </tr>
 ))}
  {memberList.length === 0 && (
 <tr>
 <td colSpan={7} className="px-6 py-4 text-center text-text-secondary">No overdue members found.</td>
 </tr>
 )}
 </tbody>
 </table>
 </Card>
 )}

 {selectedInstallment && (
 <LogReminderModal
 installmentId={selectedInstallment.installmentId}
 memberId={selectedInstallment.memberId}
 onClose={() => setSelectedInstallment(null)}
 />
 )}
 </div>
 );
}

function LogReminderModal({ installmentId, memberId, onClose }: { installmentId: string, memberId: string, onClose: () => void }) {
 const { mutate, isPending } = useCreateContactAttempt(installmentId);
 const [channel, setChannel] = useState<'sms' | 'whatsapp' | 'call_note'>('call_note');
 const [outcome, setOutcome] = useState('');
 const [notes, setNotes] = useState('');

 const handleSubmit = (e: React.FormEvent) => {
 e.preventDefault();
 mutate({ memberId, channel, outcome, notes }, {
 onSuccess: () => {
 onClose();
 }
 });
 };

 return (
 <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-modal">
 <Card className="max-w-md w-full">
 <CardHeader className="text-xl font-bold mb-4">Log Reminder</CardHeader>
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <label className="block text-sm font-medium mb-1">Channel</label>
 <select 
 value={channel} 
 onChange={e => setChannel(e.target.value as any)}
 className="w-full border rounded-md p-2"
 >
 <option value="call_note">Call</option>
 <option value="sms">SMS</option>
 <option value="whatsapp">WhatsApp</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium mb-1">Outcome</label>
 <select 
 value={outcome} 
 onChange={e => setOutcome(e.target.value)}
 className="w-full border rounded-md p-2"
 required
 >
 <option value="">Select Outcome</option>
 <option value="Promised to Pay">Promised to Pay</option>
 <option value="Unreachable">Unreachable</option>
 <option value="Refused">Refused</option>
 <option value="Escalate">Escalate</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium mb-1">Notes</label>
 <textarea 
 value={notes} 
 onChange={e => setNotes(e.target.value)}
 className="w-full border rounded-md p-2"
 rows={3}
 />
 </div>
 <div className="flex justify-end space-x-2 pt-4">
 <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
 <Button type="submit" variant="primary" disabled={isPending}>
 {isPending ? 'Saving...' : 'Save Contact Attempt'}
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 );
}
